import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X, Pencil } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { salesTargetService } from '../../services/sales-target.service';
import { userService } from '../../services/user.service';
import type { SalesTarget, SalesTargetInput } from '../../types/crm';
import type { User, PaginationMeta } from '../../types';
import Loading from '../../components/Loading';

const salesTargetSchema = z.object({
  user_id: z.number().int().positive('Rep is required'),
  target_month: z.string().min(1, 'Target month is required'),
  target_amount: z.string().min(1, 'Target amount is required'),
  target_cases: z.string().optional(),
  notes: z.string().optional(),
});

type SalesTargetFormData = z.infer<typeof salesTargetSchema>;

const todayFirstOfMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const DEFAULT_FORM: SalesTargetFormData = {
  user_id: 0,
  target_month: todayFirstOfMonth(),
  target_amount: '',
  target_cases: '',
  notes: '',
};

const toInput = (data: SalesTargetFormData): SalesTargetInput => ({
  user_id: data.user_id,
  target_month: data.target_month,
  target_amount: data.target_amount,
  target_cases: data.target_cases?.trim() ? data.target_cases.trim() : null,
  notes: data.notes?.trim() ? data.notes.trim() : null,
});

export const SalesTargets = () => {
  const [items, setItems] = useState<SalesTarget[]>([]);
  const [, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [userFilter, setUserFilter] = useState<number | null>(null);
  const [monthFrom, setMonthFrom] = useState('');
  const [monthTo, setMonthTo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<SalesTarget | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SalesTargetFormData>({
    resolver: zodResolver(salesTargetSchema),
    defaultValues: DEFAULT_FORM,
  });

  const loadTargets = useCallback(async () => {
    setIsLoading(true);
    try {
      const q: Record<string, unknown> = {};
      if (userFilter) q['user_id_eq'] = userFilter;
      if (monthFrom) q['target_month_gteq'] = monthFrom;
      if (monthTo) q['target_month_lteq'] = monthTo;

      const result = await salesTargetService.getAll({ q });
      setItems(result.data);
      setMeta(result.meta);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [userFilter, monthFrom, monthTo]);

  const loadUsers = useCallback(async () => {
    try {
      const all = await userService.getAllUsers();
      setUsers(all.filter((u) => u.active));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    loadTargets();
  }, [loadTargets]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_FORM);
    setShowModal(true);
  };

  const openEdit = (target: SalesTarget) => {
    setEditing(target);
    reset({
      user_id: target.user.id,
      target_month: target.target_month,
      target_amount: target.target_amount,
      target_cases: target.target_cases ?? '',
      notes: target.notes ?? '',
    });
    setShowModal(true);
  };

  const onSubmit = async (data: SalesTargetFormData) => {
    setIsSaving(true);
    try {
      const payload = toInput(data);
      if (editing) {
        await salesTargetService.update(editing.id, payload);
        toast.success('Sales target updated');
      } else {
        await salesTargetService.create(payload);
        toast.success('Sales target created');
      }
      setShowModal(false);
      reset(DEFAULT_FORM);
      loadTargets();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (target: SalesTarget) => {
    if (!confirm(`Delete sales target for ${target.user.full_name}?`)) return;
    try {
      await salesTargetService.delete(target.id);
      toast.success('Sales target deleted');
      loadTargets();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Targets</h1>
          <p className="text-sm text-gray-600 mt-1">Monthly revenue goals per rep</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Add Target
        </button>
      </div>

      {/* Filters */}
      <div className="card grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="label">Rep</label>
          <select
            className="input"
            value={userFilter ?? ''}
            onChange={(e) => setUserFilter(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">All reps</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Month from</label>
          <input
            type="date"
            className="input"
            value={monthFrom}
            onChange={(e) => setMonthFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Month to</label>
          <input
            type="date"
            className="input"
            value={monthTo}
            onChange={(e) => setMonthTo(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <div className="card text-center text-gray-500 py-12">No sales targets found.</div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rep</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cases</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((target) => (
                  <tr key={target.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {target.user.full_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {target.target_month}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      ${target.target_amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      {target.target_cases ?? '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {target.notes ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                      <button
                        onClick={() => openEdit(target)}
                        className="text-blue-600 hover:text-blue-900"
                        aria-label={`Edit sales target for ${target.user.full_name}`}
                      >
                        <Pencil className="h-4 w-4 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(target)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setShowModal(false)}
            />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editing ? 'Edit Sales Target' : 'Add Sales Target'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="user_id" className="label">Rep</label>
                      <select
                        {...register('user_id', { valueAsNumber: true })}
                        id="user_id"
                        className="input"
                      >
                        <option value={0}>Select a rep…</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.full_name}
                          </option>
                        ))}
                      </select>
                      {errors.user_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.user_id.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="target_month" className="label">Target month</label>
                      <input
                        {...register('target_month')}
                        id="target_month"
                        type="date"
                        className="input"
                      />
                      <p className="text-xs text-gray-500 mt-1">First of the month, e.g. 2026-06-01</p>
                      {errors.target_month && (
                        <p className="mt-1 text-sm text-red-600">{errors.target_month.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="target_amount" className="label">Amount ($)</label>
                        <input
                          {...register('target_amount')}
                          id="target_amount"
                          type="text"
                          inputMode="decimal"
                          className="input"
                          placeholder="10000.00"
                        />
                        {errors.target_amount && (
                          <p className="mt-1 text-sm text-red-600">{errors.target_amount.message}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="target_cases" className="label">Cases (optional)</label>
                        <input
                          {...register('target_cases')}
                          id="target_cases"
                          type="text"
                          inputMode="decimal"
                          className="input"
                          placeholder="250"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="notes" className="label">Notes</label>
                      <textarea {...register('notes')} id="notes" rows={3} className="input" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isSaving} className="btn btn-primary">
                    {isSaving ? 'Saving…' : editing ? 'Save Changes' : 'Create Target'}
                  </button>
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTargets;
